<!--
  PostDetailPage — /posts/:id/ — one post, by id.

  The focusable deep-link target the backend asked for. `product.unavailable` is
  the first FAN-OUT notification in the registry — one club event, N recipients,
  and the recipient's own carton is not involved — so its tap has nowhere
  order-shaped to land. Today `route()` returns `/home/`, which drops the member
  on a cursor-paginated feed to hunt for the product herself. This route is the
  reply: one resource by id, resolving regardless of feed pagination state.

  See ../backend/_bmad-output/sync/briefs/2026-08-01-fe-post-detail-deeplink-and-
  notification-data.md — the backend flips its route arm to /posts/{postId}/ once
  this is on main, and is STOP-gated on the follow-up brief carrying the SHA.

  ROUTE INVARIANTS (playbook Section 5, mirrors IssueDecisionPage / SupplierBoxDetailPage):
   - EAGER `component:` in routes.js — a push tap / PWA cold-launch deep-link can
     make this the boot URL; `asyncComponent:` would paint the literal
     "[object Promise]" on that first render (row 16).
   - Guarded by requireMembership; an unauthenticated cold hard-load is bounced to
     /login/ by App.vue's EAGER_MEMBER_REQUIRED_PREFIXES pre-empt BEFORE mount
     (row 22 — an eager component rendering an <f7-page> takes F7's DOM-init path
     on the initial URL and skips beforeEnter entirely).
   - `meta.hideTabbar: true` — one post owns the viewport.
   - The trailing-slash normalizer is the FUNCTION form, AFTER the strict route
     (row 20 — path-to-regexp v6 asymmetry).

  RENDERING: the shipped PostCard, unchanged. It already renders standalone
  outside the virtualizer (SupplierFeedPage does it), and reading through the
  same component is what guarantees a deep-linked post shows the SAME
  availability badge / last-collected line as the feed and the bookmarks tab.

  A post that no longer resolves — deleted, or outside this member's club — lands
  on a warm not-found, never a crash and never a raw error code.

  `:page-content="false"` on <f7-page>: the page brings its OWN <f7-page-content>
  (for pull-to-refresh). Without the prop <f7-page> wraps that one in a second
  .page-content and both get the navbar's padding, so everything starts one
  navbar-height too low (a blank band under the navbar; measured 76px).
  It also puts <product-suggestion-host> where its comment says it is: a child
  of the page, outside every .page-content.
-->
<template>
  <f7-page name="post-detail" :page-content="false" @page:beforein="onBeforeIn">
    <f7-navbar :title="t('product.detail.title')" no-hairline>
      <template #left>
        <f7-link class="post-detail__back" @click="leave">
          <f7-icon f7="chevron_right" size="22"></f7-icon>
          <span class="post-detail__back-label">{{ t('general.back') }}</span>
        </f7-link>
      </template>
    </f7-navbar>

    <f7-page-content ptr ptr-mousewheel @ptr:refresh="onPtr">
      <div class="post-detail__wrap">
        <!-- LOADING — the shipped feed skeleton, so the shape the member waits
             on is the shape that arrives. -->
        <skeleton-card v-if="state === 'loading'"></skeleton-card>

        <!-- NOT FOUND — deleted, or not visible to this member. Warm, and it
             offers the one thing that always works: the feed. -->
        <div v-else-if="state === 'not-found'" class="post-detail__empty">
          <f7-icon f7="doc_text_search" size="56" class="post-detail__empty-icon"></f7-icon>
          <p class="post-detail__empty-title">{{ t('product.detail.notFound.title') }}</p>
          <p class="post-detail__empty-body">{{ t('product.detail.notFound.body') }}</p>
          <f7-button fill round class="post-detail__empty-action" @click="goHome">
            {{ t('product.detail.notFound.action') }}
          </f7-button>
        </div>

        <!-- ERROR — a transient failure, distinct from not-found: it offers a
             retry, because retrying is the thing that can actually work here. -->
        <div v-else-if="state === 'error'" class="post-detail__empty">
          <f7-icon f7="wifi_slash" size="56" class="post-detail__empty-icon"></f7-icon>
          <p class="post-detail__empty-body">{{ t('product.detail.error') }}</p>
          <f7-button fill round class="post-detail__empty-action" @click="load">
            {{ t('general.retry') }}
          </f7-button>
        </div>

        <post-card
          v-else-if="postId"
          :post-id="postId"
          current-feed="posts"
          @channel-click="onChannelClick"
        ></post-card>
      </div>
    </f7-page-content>

    <!-- The card above carries the «تعرف مين عنده؟» door, and every door needs a
         host — one sheet, at page level. Kept OUTSIDE .f7-page-content for the
         same reason it is outside the feed's rows on HomePage. -->
    <product-suggestion-host></product-suggestion-host>
  </f7-page>
</template>

<script setup>
import { useAxiosRepo } from '@pinia-orm/axios';
import { useRepo } from 'pinia-orm';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PostCard from '@/components/posts/PostCard.vue';
import ProductSuggestionHost from '@/components/posts/ProductSuggestionHost.vue';
import SkeletonCard from '@/components/posts/SkeletonCard.vue';
import Post from '@/models/posts/Post.js';
import { channelTapTarget } from '@/utils/tadbeesa.js';

// F7-Vue passes f7route / f7router as props to `component:` routes.
const props = defineProps({
  f7route: { type: Object, required: true },
  f7router: { type: Object, default: null },
});

const { t } = useI18n({ useScope: 'global' });

/**
 * The SAME include set the feed sends (HomePage's usePostsFeed config and
 * useCart.fetchPostData both use it verbatim) so a deep-linked post hydrates
 * identically to a scrolled-to one.
 *
 * NO `fields[posts]`. A sparse fieldset silently trims any attribute the JSON:API
 * schema does not declare — and `availability` / `lastCollected` / `isNew` are
 * exactly that: additive resource attributes that ride outside PostSchema's
 * declared fields. Sending one here would strip the badge this route exists to
 * deep-link to. Do not add one.
 */
const INCLUDE = 'mediables,media,channel,products,categories,products.variants';

const state = ref('loading'); // 'loading' | 'ready' | 'not-found' | 'error'

const postId = computed(() => props.f7route?.params?.id ?? null);

async function load() {
  const id = postId.value;
  if (!id) {
    state.value = 'not-found';
    return;
  }

  state.value = 'loading';
  try {
    const response = await useAxiosRepo(Post).api().get(`/posts/${id}`, {
      params: { include: INCLUDE, withCount: 'bookmarks' },
      save: false,
    });

    const data = response?.response?.data?.data;
    if (!data) {
      state.value = 'not-found';
      return;
    }

    useRepo(Post).save(data);
    state.value = 'ready';
  } catch (err) {
    // 404 (deleted) and 403 (another club's post) are the same thing to the
    // member: this post is not there for her. Everything else is transient and
    // gets a retry. A 401 never reaches here — the axios interceptor's
    // `auth:unauthorized` event owns that path.
    const status = err?.response?.status;
    state.value = status === 404 || status === 403 ? 'not-found' : 'error';
  }
}

// How she arrived decides where the back link goes. router.back() is only safe
// after an in-app push. On a cold launch from a push, a reload or a pasted link,
// F7's browserHistory boot FABRICATES a history of ['/', '/posts/:id/']
// (framework7/modules/router/router-class.js getInitialUrl) and back() then
// calls the browser's History.back() — which leaves the app (about:blank in a
// fresh tab). `router.previousRoute` is set on a cold load too, so it cannot
// tell the cases apart. F7 reports how the page arrived: `from: 'next'` only
// for a forward push (navigate.js); a boot or reload arrives as 'current'.
// Mirrors TadbeesaPage.vue.
let pushedInApp = false;

// Single fetch on first entry, refresh on every re-entry — the availability
// facts are club state that can change while the member is elsewhere, and this
// surface has no realtime rail.
let firstBeforeIn = true;
function onBeforeIn(page) {
  pushedInApp = page?.from === 'next';
  if (firstBeforeIn) {
    firstBeforeIn = false;
    load();
    return;
  }
  load();
}

async function onPtr(event, done) {
  await load();
  done?.();
}

function goHome() {
  props.f7router?.navigate('/', { clearPreviousHistory: true });
}

/** A «تدبيسة» post's header opens the club's shelf; any other channel's is a no-op. */
function onChannelClick(_channelId, meta) {
  const target = channelTapTarget(meta);
  if (target) props.f7router?.navigate(target);
}

/** Back where she came from; the feed when she arrived by a push launch or a
 *  reload — stranding her outside the app is the bug this route exists to avoid. */
function leave() {
  const r = props.f7router;
  if (pushedInApp && r?.history?.length > 1) r.back();
  else goHome();
}
</script>

<style scoped>
.post-detail__wrap {
  padding: 8px 0 32px;
}

.post-detail__back {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.post-detail__back-label {
  font-size: 15px;
}

.post-detail__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 56px 28px;
}

.post-detail__empty-icon {
  color: var(--poolit-text-tertiary);
}

.post-detail__empty-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: var(--poolit-text-primary);
}

.post-detail__empty-body {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--poolit-text-secondary);
}

.post-detail__empty-action {
  margin-top: 8px;
  min-width: 180px;
}
</style>
